// Trend chart: one or two measures over time, for the admin dashboard.
//
// The job is change-over-time, so it is a line with a soft area under it —
// thin 2px marks, a recessive grid, and labels only where they earn their
// place. Two series share ONE axis because they are the same unit (counts); a
// second y-scale would let us draw any story we liked out of the same numbers.
//
// It is interactive by default: a chart in a browser that cannot tell you the
// value under your cursor is a picture, not a chart. Hover moves a crosshair
// and a tooltip; the whole plot is one pointer target, so there is nothing to
// hit precisely.
//
// Colour carries IDENTITY here (meetings vs calls), never rank — the same
// series keeps the same colour whatever the numbers do.

import { memo, PointerEvent, useCallback, useMemo, useState } from 'react';

import { Pt, smoothPath } from '../lib/curve';
import { useMorphedValues } from '../lib/morph';

export interface TrendSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

// Two sizes, because SVG text scales with the viewBox: a 960-wide chart squeezed
// into a 320px card would render its labels at ~4px. The compact geometry keeps
// type legible in the small System cards.
const SIZES = {
  full: { W: 960, H: 260, PAD: { top: 16, right: 18, bottom: 26, left: 44 } },
  compact: { W: 420, H: 150, PAD: { top: 12, right: 10, bottom: 20, left: 30 } },
} as const;

/** A round number at or above the peak, so the axis reads 40 rather than 37. */
function niceMax(v: number): number {
  if (v <= 5) return 5;
  const mag = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / mag) * mag;
}

/** Keep at most `keep` x labels — a label per pixel is noise, not information. */
function labelStride(n: number, keep = 8): number {
  return Math.max(1, Math.ceil(n / keep));
}

/** A coloured dot. Takes a primitive so the style object lives here, memoized,
 * instead of being rebuilt in every parent render. */
export const SeriesDot = memo(function SeriesDot({
  color,
  className = 'bz-dash-legend-dot',
}: {
  color: string;
  className?: string;
}) {
  const style = useMemo(() => ({ background: color }), [color]);
  return <span className={className} style={style} />;
});

export const TrendChart = memo(function TrendChart({
  labels,
  series,
  format,
  size = 'full',
}: {
  labels: string[];
  series: TrendSeries[];
  format?: (v: number) => string;
  size?: keyof typeof SIZES;
}) {
  const [hover, setHover] = useState<number | null>(null);

  // The drawn values, tweened from whatever is currently on screen: switching
  // 1h → 24h or Weekly → Monthly should look like this chart changing, not like
  // a different chart appearing.
  const targets = useMemo(() => series.map((s) => s.values), [series]);
  const drawn = useMorphedValues(targets);
  const shown = useMemo(
    () => series.map((s, i) => ({ ...s, values: drawn[i] ?? s.values })),
    [series, drawn],
  );

  const { W, H, PAD } = SIZES[size];
  const { top: padTop, left: padLeft, right: padRight, bottom: padBottom } = PAD;
  const n = labels.length;
  const iw = W - padLeft - padRight;
  const ih = H - padTop - padBottom;
  const step = n > 1 ? iw / (n - 1) : 0;

  // Max follows the tweened values, so the axis grows and shrinks with the
  // shape instead of snapping the moment the data changes.
  const max = useMemo(
    () => niceMax(Math.max(1, ...shown.flatMap((s) => s.values))),
    [shown],
  );
  const x = useCallback((i: number) => padLeft + i * step, [step, padLeft]);
  const y = useCallback((v: number) => padTop + ih - (v / max) * ih, [ih, max, padTop]);

  const paths = useMemo(
    () =>
      shown.map((s) => {
        const pts: Pt[] = s.values.map((v, i) => [x(i), y(v)]);
        // Rounded, but monotone — the curve can never rise above or fall below
        // the points it joins, so a spike stays a spike and zero stays zero.
        const line = smoothPath(pts);
        const area =
          pts.length > 1
            ? `${line} L${pts[pts.length - 1]![0].toFixed(1)},${padTop + ih} L${pts[0]![0].toFixed(1)},${padTop + ih} Z`
            : '';
        return { ...s, line, area };
      }),
    [shown, x, y, ih, padTop],
  );

  // One pointer target for the whole plot: snap to the nearest column.
  const onMove = useCallback(
    (e: PointerEvent<SVGSVGElement>) => {
      const r = e.currentTarget.getBoundingClientRect();
      const px = ((e.clientX - r.left) / r.width) * W;
      const i = Math.round((px - padLeft) / (step || 1));
      setHover(i >= 0 && i < n ? i : null);
    },
    [step, n, padLeft, W],
  );
  const onLeave = useCallback(() => setHover(null), []);

  const tipStyle = useMemo(
    () => (hover === null ? undefined : { left: `${(x(hover) / W) * 100}%` }),
    [hover, x, W],
  );
  const ticks = [0, 0.5, 1].map((f) => Math.round(max * f));
  const stride = labelStride(n, size === 'compact' ? 3 : 8);

  return (
    <div className={size === 'compact' ? 'bz-trend bz-trend-compact' : 'bz-trend'}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={size === 'compact' ? 'bz-trend-svg bz-trend-svg-compact' : 'bz-trend-svg'}
        role="img"
        aria-label={series.map((s) => s.label).join(' and ') + ' over time'}
        onPointerMove={onMove}
        onPointerLeave={onLeave}
      >
        <defs>
          {paths.map((s) => (
            <linearGradient key={s.key} id={`bz-trend-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {/* Grid: present enough to read a value off, quiet enough to ignore. */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padLeft} y1={y(t)} x2={W - padRight} y2={y(t)} className="bz-trend-grid" />
            <text x={padLeft - 10} y={y(t) + 4} className="bz-trend-ylabel">
              {t}
            </text>
          </g>
        ))}

        {paths.map((s) => (
          <path key={`a-${s.key}`} d={s.area} fill={`url(#bz-trend-${s.key})`} />
        ))}
        {paths.map((s) => (
          <path
            key={`l-${s.key}`}
            d={s.line}
            className="bz-trend-line"
            stroke={s.color}
            fill="none"
          />
        ))}

        {hover !== null && (
          <g className="bz-trend-hover">
            <line x1={x(hover)} y1={padTop} x2={x(hover)} y2={padTop + ih} className="bz-trend-cross" />
            {shown.map((s) => (
              <circle key={s.key} cx={x(hover)} cy={y(s.values[hover] ?? 0)} r="4.5" fill={s.color} className="bz-trend-dot" />
            ))}
          </g>
        )}

        {labels.map((l, i) =>
          // The final label is worth keeping, but not if it lands on top of the
          // one before it — that is how "Aug 17Aug 18" happens.
          i % stride === 0 ? (
            <text key={i} x={x(i)} y={H - 8} className="bz-trend-xlabel">
              {l}
            </text>
          ) : null,
        )}
        {n > 1 && (n - 1) % stride > stride / 2 && (
          <text x={x(n - 1)} y={H - 8} className="bz-trend-xlabel">
            {labels[n - 1]}
          </text>
        )}
      </svg>

      {hover !== null && (
        <div className="bz-trend-tip" style={tipStyle} role="status">
          <div className="bz-trend-tip-when">{labels[hover]}</div>
          {series.map((s) => (
            <div key={s.key} className="bz-trend-tip-row">
              <SeriesDot color={s.color} className="bz-trend-tip-dot" />
              {s.label}
              <b>{format ? format(s.values[hover] ?? 0) : (s.values[hover] ?? 0)}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
